import { useState } from 'react';

import { twoFaVerifyUsingOauth } from 'actions/other';
import { DISABLE_2FA_FLOW } from 'components/Disable2faFaceAuth/Disable2faFaceAuth.constants';
import { UpdateWorkflowData } from 'components/Disable2faFaceAuth/Disable2faFaceAuth.types';
import { OAUTH_PROVIDERS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import useApi from 'hooks/useApi';
import i18n from 'i18n/config';
import { useAppDispatch } from 'storeHooks';
import { TwoFactorDisableOtpVerificationResponse } from 'types/actions/other';

import { BACKEND_ERROR_CODES } from './useVerifyUsingOauth.constants';
import { AllErrors } from './useVerifyUsingOauth.types';

const useVerifyUsingOauth = ({
  updateActiveFlow,
  updateWorkflowData,
}: {
  updateActiveFlow: (flow: DISABLE_2FA_FLOW, options?: { message?: string }) => void;
  updateWorkflowData: UpdateWorkflowData;
}) => {
  const [localProvider, setLocalProvider] = useState<OAUTH_PROVIDERS | null>(null);

  const appDispatch = useAppDispatch();

  const { requestor, isLoading, reset, error } = useApi<
    {
      accessToken: string;
      provider: OAUTH_PROVIDERS;
    },
    TwoFactorDisableOtpVerificationResponse['result'],
    AllErrors
  >({
    requestFn: ({ accessToken, provider }) =>
      appDispatch(
        twoFaVerifyUsingOauth({ data: { oauth_token: accessToken, oauth: provider } })
      ),
    onSuccess: data => {
      updateWorkflowData({
        authToken: data.auth_token,
        workflowId: data.faceAuth_workflow_id,
        transactionId: data.transaction_id,
        vendor: data.vendor,
        uid: data.uid,
        recoveryId: data.recovery_id,
      });
      updateActiveFlow(DISABLE_2FA_FLOW.FACE_AUTH);

      trackMixpanelEvent(
        MIXPANEL_EVENT_NAMES.DISABLE_2FA_WITH_FACE_AUTH.EMAIL_VERIFIICATION_SUCCESS,
        {
          type: 'Face Auth',
          source: MIXPANEL_SOURCES.LOGIN_PAGE,
          verification_type: 'oauth',
        }
      );
    },
    onError: err => {
      if (err.code === BACKEND_ERROR_CODES.KYC_NOT_APPROVED) {
        updateActiveFlow(DISABLE_2FA_FLOW.RAISE_TICKET);
      }

      if (
        [
          BACKEND_ERROR_CODES.FAILED_TO_ENROL_HYPERVERGE,
          BACKEND_ERROR_CODES.HYPERVERGE_ACCOUNT_ENROL_FAILED,
          BACKEND_ERROR_CODES.S3_ERROR,
        ].includes(err.code)
      ) {
        updateActiveFlow(DISABLE_2FA_FLOW.RAISE_TICKET, {
          message: i18n.t<string>('errors:selfieNotClearRaiseTicketDisable2fa'),
        });
      }

      if (err.code === BACKEND_ERROR_CODES.KYC_NOT_APPROVED) {
        trackMixpanelEvent(
          MIXPANEL_EVENT_NAMES.DISABLE_2FA_WITH_FACE_AUTH.EMAIL_VERIFIICATION_SUCCESS,
          {
            type: 'ticket',
            source: MIXPANEL_SOURCES.LOGIN_PAGE,
            verification_type: 'oauth',
          }
        );
      }
    },
  });

  return {
    localProvider,
    setLocalProvider,
    requestor,
    isLoading,
    reset,
    error,
  };
};

export default useVerifyUsingOauth;
